import type { ReactNode } from '@frontend/react';
import { useTerminology, type PricingMode, type SubscriptionTerminologyConfig } from '@frontend/react-domain';
import { useRetrieveClientQuery, useRetrieveClientStatusQuery } from '@frontend/web/hooks';
import { createContext, useContext } from 'react';

type PricingTerminologyContextValue = {
  mode: PricingMode;
  pricingTerminology: SubscriptionTerminologyConfig;
  showCredits: boolean;
};

const PricingTerminologyContext = createContext<PricingTerminologyContextValue | null>(null);

/**
 * Hook to access pricing terminology from context.
 * Must be used within a PricingTerminologyProvider.
 *
 * @example
 * const { pricingTerminology, showCredits } = usePricingTerminology();
 */
export const usePricingTerminology = (): PricingTerminologyContextValue => {
  const context = useContext(PricingTerminologyContext);
  if (!context) {
    throw new Error('usePricingTerminology must be used within a PricingTerminologyProvider');
  }
  return context;
};

type PricingTerminologyProviderProps = {
  children: ReactNode;
};

/**
 * Provider that fetches client data and makes pricing terminology
 * available to all child components via context.
 *
 * Place this at the authenticated app boundary (e.g., /app route).
 *
 * @example
 * <PricingTerminologyProvider>
 *   <App />
 * </PricingTerminologyProvider>
 */
export const PricingTerminologyProvider = ({ children }: PricingTerminologyProviderProps) => {
  const { data: client } = useRetrieveClientQuery();
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const terminology = useTerminology({ client, clientStatus });

  return <PricingTerminologyContext.Provider value={terminology}>{children}</PricingTerminologyContext.Provider>;
};
